<template>
    <section id="company">
        <div class="page-breadcrumbs">
            <page-breadcrumbs :list="breadcrumbs" />
        </div>
        <h2 class="page-title">
            Companies
        </h2>
        <el-row
            :gutter="15"
            justify="space-between"
        >
            <el-col class="col-auto">
                <base-filter v-model="filters" />
            </el-col>
            <el-col
                v-if="$auth.check('companies.create')"
                class="col-auto"
            >
                <el-button
                    type="primary"
                    icon="plus"
                    @click="$router.push({name: 'companies.add'})"
                >
                    Add
                </el-button>
            </el-col>
        </el-row>

        <!-- table -->
        <el-table
            v-loading="companiesLoading"
            :data="companies"
            highlight-current-row
            class="w-100"
            @sort-change="handleSortChange"
            @filter-change="handleFilterChange"
        >
            <el-table-column
                prop="name"
                label="Name"
                min-width="200"
            />
            <el-table-column
                :sort-orders="sortOrders"
                prop="updated_at"
                label="Updated"
                width="200"
            >
                <template #default="scope">
                    {{ $formatDate(scope.row.updated_at, 'YYYY-MM-DD HH:mm') }}
                </template>
            </el-table-column>
            <el-table-column
                label="Actions"
                min-width="180"
                align="right"
            >
                <template #default="scope">
                    <el-button
                        v-if="$auth.check('companies.edit')"
                        size="small"
                        icon="edit"
                        @click="$router.push({name: 'companies.edit', params: {id: scope.row._id}})"
                    >
                        Edit
                    </el-button>
                    <el-button
                        v-if="$auth.check('companies.delete')"
                        type="danger"
                        size="small"
                        icon="delete"
                        @click="handleDelete(scope.row)"
                    >
                        Delete
                    </el-button>
                </template>
            </el-table-column>
        </el-table>
        <el-pagination
            v-model:current-page="page"
            :page-size="per_page"
            :total="total"
            hide-on-single-page
            layout="prev, pager, next, ->, total"
            class="el-pagination--align-right"
            @current-change="fetchData"
            @size-change="fetchData"
        />
    </section>
</template>

<script setup>
import {ref, onMounted, watch} from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import companyApi from '../companyApi'
import BaseFilter from '~/base/components/filters/BaseFilter'
import PageBreadcrumbs from '~/base/components/page-breadcrumbs/PageBreadcrumbs'

const companiesLoading = ref(false)
const companies = ref(null)
const total = ref(0)
const sortBy = ref('id,asc')
const sortOrders = ref(['ascending', 'descending'])
const filters = ref({search: ''})
const page = ref(1)
const per_page = ref(10)
const breadcrumbs = ref([
    {
        title: '..',
        path: '/'
    },
    {
        title: 'Companies',
    }
])

onMounted(() => fetchData())

watch(
    () => filters.value,
    val => applySearch(),
    { deep: true }
)

function handleSortChange(val) {
    if (val.prop != null && val.order != null) {
        let sort = val.order.startsWith('a') ? 'asc' : 'desc'
        sortBy.value = val.prop + ',' + sort
        fetchData()
    }
}
function handleFilterChange() {
    fetchData()
}

async function fetchData() {
    companiesLoading.value = true
    let params = {
        page: page.value,
        sortBy: sortBy.value,
        ...filters.value
    }
    const {data} = await companyApi.all(params)
    companies.value = data.data
    total.value = data.total
    per_page.value = data.per_page
    companiesLoading.value = false
}

function handleDelete(row) {
    ElMessageBox.confirm('This will permanently delete the company. Continue?', 'Warning', {
        type: 'warning'
    }).then(() => {
        companyApi.destroy(row).then((response) => {
            ElMessage.success( 'Deleted successfully')
            fetchData()
        })
    })
}

const applySearch = _.debounce( function() {
    fetchData()
}, 300)
</script>
